import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider } from "./auth/AuthProvider";
import PrivateRoute from "./auth/PrivateRoute";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import SchedulePage from "./pages/SchedulePage";
import ResourcesPage from "./pages/ResourcesPage";
import Layout from "./components/Layout";
import EventDetailsPageTemplateTwo from "./pages/EventDetailsPageTemplateTwo";

import "./i18n/i18n";
import "./auth/cognitoConfig";
import "./styles/App.css";

// Pages
import SplashScreen from "./pages/SplashScreen";
import LoginPage from "./pages/LoginPage";
import LoginOTPPage from "./pages/LoginOTPPage";
import HomePage from "./pages/HomePage";
import EventDetailsPage from "./pages/EventDetailsPage";
import CallbackPage from "./pages/CallbackPage";
import ProfilePage from "./pages/ProfilePage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminQuizResultsPage from "./pages/AdminQuizResultsPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminReportsPage from "./pages/AdminReportsPage";
import AdminRoute from "./auth/AdminRoute";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import CertificatePage from './pages/CertificatePage';
import AdminCertificatePage from './pages/AdminCertificatePage';

// Event Registration Pages
import EventRegistrationPage from "./pages/EventRegistrationPage";
import RegistrationSuccessPage from "./pages/RegistrationSuccessPage";
// Admin pages
import ModuleDetailsPage from "./pages/ModuleDetailsPage";

import { AudioPlayerProvider } from "./context/AudioPlayerContext";
import { LanguageProvider } from "./context/LanguageContext";
import { UserProvider } from "./context/UserProvider";
import { QuizSummaryProvider } from "./context/QuizSummaryContext";
import { ModuleProvider } from "./context/ModuleContext";

const queryClient = new QueryClient();

function App() {
  useEffect(() => {
    document.title = "Sai Sruti - Timeless Wisdom For The Modern Man";
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProvider>
          <LanguageProvider>
            <QuizSummaryProvider>
              <ModuleProvider>
                <AudioPlayerProvider>
                  <Router>
                    <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<SplashScreen />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/login-otp" element={<LoginOTPPage />} />
                    <Route path="/callback" element={<CallbackPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/admin/login" element={<AdminLoginPage />} />

                    {/* Protected routes */}
                    <Route
                      path="/admin/dashboard"
                      element={
                        <AdminRoute>
                          <Layout>
                            <AdminDashboardPage />
                          </Layout>
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/quiz-results"
                      element={
                        <AdminRoute>
                          <Layout>
                            <AdminQuizResultsPage />
                          </Layout>
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <AdminRoute>
                          <Layout>
                            <AdminUsersPage />
                          </Layout>
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/certificate"
                      element={
                        <AdminRoute>
                          <Layout>
                            <AdminCertificatePage />
                          </Layout>
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/reports"
                      element={
                        <AdminRoute>
                          <Layout>
                            <AdminReportsPage />
                          </Layout>
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/home"
                      element={
                        <PrivateRoute>
                          <Layout>
                            <HomePage />
                          </Layout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <PrivateRoute>
                          <Layout>
                            <ProfilePage />
                          </Layout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/registration"
                      element={
                        <Layout>
                          <EventRegistrationPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/registration-success"
                      element={
                        <Layout>
                          <RegistrationSuccessPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/event/:id"
                      element={
                        <PrivateRoute>
                          <Layout>
                            <EventDetailsPage />
                          </Layout>
                        </PrivateRoute>
                      }
                    />
                  
                    <Route
                      path="/about"
                      element={
                        <Layout>
                          <AboutPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/contact"
                      element={
                        <Layout>
                          <ContactPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/schedule"
                      element={
                        <PrivateRoute>
                          <Layout>
                            <SchedulePage />
                          </Layout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/resources"
                      element={
                        <PrivateRoute>
                          <Layout>
                            <ResourcesPage />
                          </Layout>
                        </PrivateRoute>
                      }
                    />
                      <Route
                      path="/event/active/1"
                      element={
                        <PrivateRoute>
                          <Layout>
                            <EventDetailsPageTemplateTwo />
                          </Layout>
                        </PrivateRoute>
                      }
                    />

                    <Route 
  path="/certificate" 
  element={
    <PrivateRoute> <Layout>
      <CertificatePage /> </Layout>
    </PrivateRoute>
  } 
/>

                    <Route
                      path="/module/:moduleId/contents/:contentType/pdf"
                      element={
                        <PrivateRoute>
                          <Layout>
                            <ModuleDetailsPage />
                          </Layout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/module/:moduleId/:pageNumber?"
                      element={
                        <PrivateRoute>
                          <Layout>
                            <ModuleDetailsPage />
                          </Layout>
                        </PrivateRoute>
                      }
                    />

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Router>
                </AudioPlayerProvider>
              </ModuleProvider>
            </QuizSummaryProvider>
          </LanguageProvider>
        </UserProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
